import { FETCH_POST } from "../config";
import { v4 as uuidv4 } from "uuid";

/**
 * Cart Sync Service - Manages cart synchronization with backend
 * Handles both anonymous (session-based) and authenticated (customer-based) carts
 */

// Session ID management
const SESSION_KEY = "botaniksan_session_id";

/**
 * Get or create unique session ID for cart tracking
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
};

/**
 * Reset session ID - creates a new session
 * Call this after successful order completion
 */
export const resetSessionId = () => {
  const newSessionId = uuidv4();
  localStorage.setItem(SESSION_KEY, newSessionId);

  return newSessionId;
};

/**
 * Sync cart to backend after any cart operation
 * @param {Array} cartItems - Current cart items from Redux state
 * @param {Object} customer - Logged in customer (optional)
 */
export const syncCartToBackend = async (cartItems, customer = null) => {
  try {
    const sessionId = getSessionId();

    // Transform cart items to backend format
    const items = cartItems.map((item) => ({
      product_id: item.id,
      quantity: item.quantity,
    }));

    const query = `
      mutation SyncCart($session_id: String!, $items: [CartItemInput!]!, $customer_id: ID) {
        syncCart(session_id: $session_id, items: $items, customer_id: $customer_id) {
          id
          session_id
          customer {
            id
            full_name
          }
          items {
            product {
              id
              name
              price
              sale_price
            }
            quantity
            price_snapshot
          }
          total_value
          last_updated
        }
      }
    `;

    const variables = {
      session_id: sessionId,
      items: items,
      customer_id: customer ? customer.id : null,
    };

    const response = await FETCH_POST({ query, variables });

    if (response.data.errors) {
      console.error("[CartSync] Backend sync error:", response.data.errors);
      return { success: false, errors: response.data.errors };
    }

    return { success: true, cart: response.data.data.syncCart };
  } catch (error) {
    console.error("[CartSync] Sync failed:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Validate cart before checkout - CRITICAL for security
 * Ensures prices and stock are correct from backend
 */
export const validateCartBeforeCheckout = async () => {
  try {
    const sessionId = getSessionId();

    const query = `
      mutation ValidateCart($session_id: String!) {
        validateCart(session_id: $session_id) {
          valid
          items {
            product {
              id
              name
              slug
              image
              price
              sale_price
              stock
            }
            quantity
            current_price
            available_stock
            is_available
            error_message
            price_changed
            old_price
          }
          subtotal
          total_price
          errors
          warnings
        }
      }
    `;

    const response = await FETCH_POST({
      query,
      variables: { session_id: sessionId },
    });

    if (response.data.errors) {
      console.error("[CartSync] Validation error:", response.data.errors);
      return {
        success: false,
        valid: false,
        errors: response.data.errors.map((e) => e.message),
      };
    }

    const validation = response.data.data.validateCart;

    return {
      success: true,
      valid: validation.valid,
      items: validation.items,
      subtotal: validation.subtotal,
      total_price: validation.total_price,
      errors: validation.errors,
      warnings: validation.warnings,
    };
  } catch (error) {
    console.error("[CartSync] Validation failed:", error);
    return {
      success: false,
      valid: false,
      errors: [error.message],
    };
  }
};

/**
 * Fetch current prices for cart items from backend
 * @param {Array} cartItems - Array of cart items from localStorage
 * @returns {Object} - { success, items, warnings, errors }
 */
export const fetchCurrentPrices = async (cartItems) => {
  try {
    if (!cartItems || cartItems.length === 0) {
      return { success: true, items: [], warnings: [], errors: [] };
    }

    const warnings = [];
    const errors = [];

    // Fetch each product individually
    const fetchPromises = cartItems.map(async (cartItem) => {
      try {
        const query = `
          query GetProduct($id: ID!) {
            getProduct(id: $id) {
              id
              name
              price
              sale_price
              stock
              active
              image
              slug
              categories {
                id
                title
              }
            }
          }
        `;

        const result = await FETCH_POST({
          query,
          variables: { id: cartItem.id },
        });

        if (result.data.errors || !result.data.data.getProduct) {
          console.error(
            "[fetchCurrentPrices] Error fetching product:",
            result.data.errors
          );
          errors.push(cartItem.name + " ürünü bulunamadı");
          return {
            ...cartItem,
            available: false,
            current_price: cartItem.sale_price || cartItem.price,
            price_changed: false,
          };
        }

        const product = result.data.data.getProduct;
        const currentPrice = product.sale_price || product.price;
        const oldPrice = cartItem.sale_price || cartItem.price;
        const priceChanged = Math.abs(currentPrice - oldPrice) > 0.01;

        // Check price change
        if (priceChanged) {
          warnings.push(
            product.name +
              " fiyatı " +
              oldPrice.toFixed(2) +
              " TL'den " +
              currentPrice.toFixed(2) +
              " TL'ye değişti"
          );
        }

        // Check stock
        if (!product.active) {
          errors.push(product.name + " artık satışta değil");
        } else if (product.stock < cartItem.quantity) {
          errors.push(
            product.name +
              " için yeterli stok yok (Stok: " +
              product.stock +
              ", İstenen: " +
              cartItem.quantity +
              ")"
          );
        }

        return {
          ...cartItem,
          ...product,
          current_price: currentPrice,
          old_price: oldPrice,
          price_changed: priceChanged,
          available: product.active && product.stock > 0,
          stock_available: product.stock >= cartItem.quantity,
        };
      } catch (error) {
        console.error("[CartSync] Error fetching product:", cartItem.id, error);
        errors.push(cartItem.name + " bilgileri alınamadı");
        return {
          ...cartItem,
          available: false,
          current_price: cartItem.sale_price || cartItem.price,
          price_changed: false,
        };
      }
    });

    const itemsWithPrices = await Promise.all(fetchPromises);

    return {
      success: true,
      items: itemsWithPrices,
      warnings: warnings,
      errors: errors,
    };
  } catch (error) {
    console.error("[CartSync] Failed to fetch current prices:", error);
    return {
      success: false,
      items: [],
      warnings: [],
      errors: ["Fiyat bilgileri alınamadı"],
    };
  }
};

/**
 * Track cart event for analytics
 * @param {String} eventType - 'add', 'remove', 'update', 'view_cart', 'checkout_started'
 * @param {String} productId - Product ID (optional)
 * @param {Number} quantity - Quantity (optional)
 */
export const trackCartEvent = async (
  eventType,
  productId = null,
  quantity = null
) => {
  try {
    const sessionId = getSessionId();

    const query = `
      mutation TrackCartEvent(
        $session_id: String!
        $event_type: String!
        $product_id: ID
        $quantity: Int
      ) {
        trackCartEvent(
          session_id: $session_id
          event_type: $event_type
          product_id: $product_id
          quantity: $quantity
        )
      }
    `;

    const variables = {
      session_id: sessionId,
      event_type: eventType,
      product_id: productId,
      quantity: quantity,
    };

    await FETCH_POST({ query, variables });

    // Don't block user actions if tracking fails
    return true;
  } catch (error) {
    console.error("[CartSync] Event tracking failed:", error);
    return false;
  }
};

/**
 * Track product view for analytics
 */
export const trackProductView = async (productId, timeSpent = 0) => {
  try {
    const sessionId = getSessionId();

    const query = `
      mutation TrackProductView(
        $product_id: ID!
        $session_id: String!
        $time_spent: Int
      ) {
        trackProductView(
          product_id: $product_id
          session_id: $session_id
          time_spent: $time_spent
        )
      }
    `;

    const variables = {
      product_id: productId,
      session_id: sessionId,
      time_spent: timeSpent,
    };

    await FETCH_POST({ query, variables });
    return true;
  } catch (error) {
    console.error("[CartSync] Product view tracking failed:", error);
    return false;
  }
};

/**
 * Merge guest cart to customer cart after login
 * @param {String} customerId - Customer ID after login
 */
export const mergeGuestCart = async (customerId) => {
  try {
    const sessionId = getSessionId();

    const query = `
      mutation MergeGuestCart($session_id: String!, $customer_id: ID!) {
        mergeGuestCart(session_id: $session_id, customer_id: $customer_id) {
          id
          session_id
          customer {
            id
            full_name
          }
          items {
            product {
              id
              name
              price
              sale_price
            }
            quantity
            price_snapshot
          }
          total_value
        }
      }
    `;

    const response = await FETCH_POST({
      query,
      variables: { session_id: sessionId, customer_id: customerId },
    });

    if (response.data.errors) {
      console.error("[CartSync] Merge failed:", response.data.errors);
      return { success: false, errors: response.data.errors };
    }

    return { success: true, cart: response.data.data.mergeGuestCart };
  } catch (error) {
    console.error("[CartSync] Merge failed:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear session (on logout)
 */
export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};



// WEBPACK FOOTER //
// ./src/utils/cartSync.js